import React, { useState, useEffect, useCallback } from "react";

import Axios from "axios";
import moment from "moment-timezone";

import { useUser } from "../../../util/user";
import Noti from "../../../util/noti";
import GYAxios from "../../../util/GYAxios";

import InvoiceTable from "./InvoiceTable";
import SummaryInvoice from "./SummaryInvoice";
import LoaderGraph from "../../general/LoaderGraph";
import { useUserProps } from "../../../util/userDBContext";

function InvoiceContent(props) {
	const user = useUser();
	const { loggedCountry } = useUserProps();
	const [country, setCountry] = useState(loggedCountry);

	const [approvalPermissions, setApprovalPermissions] = useState(null);
	const [invoiceSites, setInvoiceSites] = useState(null);
	const [summaryInvoiceSites, setSummaryInvoiceSites] = useState(null);
	const [isMakroUser, setIsMakroUser] = useState(false);
	const [isCFWUser, setIsCFWUser] = useState(false);
	const [isGYUser, setIsGYUser] = useState(false);
	const [isSoloSite, setSoloSite] = useState(false);
	const [userInfo, setUserInfo] = useState(null);
	const [freeSites, setFreeSites] = useState([]);
	const [selectedMonth, setSelectedMonth] = useState(
		moment().subtract(1, "month")
	);
	const [ready, setReady] = useState(false);

	/**
	 * Validate user's level of invoice approval
	 * @param {User's roles'} roles
	 */
	const validateApprovalLevel = (roles) => {
		const approvalPermissions = roles.filter((role) =>
			role.includes("administration:InvoiceTable:_approveInvoice")
		);
		const hasApprovalPermission = approvalPermissions.length > 0;

		if (hasApprovalPermission || roles.includes("_GY:IT")) {
			let dept = "";
			// O&M access to approve invoice
			if (roles.includes("_GY:O&M")) dept = "O&M";
			// Accounting access to approve invoice
			else if (roles.includes("_GY:Accounting") || roles.includes("_GY:Construction_InvoiceApproval")) dept = "ACC"; // in PH, Construction team could approve invoice like Accounting in VN
			// IT access to view and resolve rejected invoice
			else if (roles.includes("_GY:IT")) dept = "IT";

			const reqApprovalStatus = GYAxios.get(
				`/general_info/approval-status/${dept}`
			);

			reqApprovalStatus.then((res) => {
				if (res.data != null) setApprovalPermissions(res.data);
			});
		} else setApprovalPermissions(false);
	};

	/**
	 * Validate if user is GY member
	 * @param {Logged in user} user
	 */
	const validateGYUser = (user) => {
		const isGYUser = user.roles.includes("_GY:Base") ? true : false;
		setIsGYUser(isGYUser);
	};

	/**
	 * Validate if logged in user is Makro
	 * @param {Logged in user} user
	 */
	const validateMakroUser = (user) => {
		const siteIDStr =
			user.site_id != null && user.site_id != "all"
				? user.site_id.toString()
				: "";
		const multiSiteID = siteIDStr.split(",");

		// validate if it is a solo site
		if (siteIDStr != "" && multiSiteID.length == 1) setSoloSite(true);
		else setSoloSite(false);
		// Validate if it is a makro user
		if (user.client_id != null && user.client_id == 18) setIsMakroUser(true);
		else setIsMakroUser(false);
	};

	/**
	 * Validate if logged in user is CFW
	 * @param {Logged in user} user
	 */
	const validateCFWUser = (user) => {
		const siteIDStr = user.site_id != null && user.site_id != "all" ? user.site_id.toString() : "";
		const multiSiteID = siteIDStr.split(",");

		// validate if it is a solo site
		if (siteIDStr != "" && multiSiteID.length == 1) setSoloSite(true);
		else setSoloSite(false);
		// Validate if it is a CFW user
		if (user.client_id != null && user.client_id == '299' && user.roles.includes("administration:InvoiceTable:_seeSummaryInvoice-client")) setIsCFWUser(true);
		else setIsCFWUser(false);  
	};

	/**
	 * Validate permission level of invoice sites accessible
	 * by user based on roles
	 * @param {Roles of user} roles
	 */
	const validateViewLevel = (roles) => {
		let level = false;

		// O&M access to approve invoice
		if (roles.includes("_GY:O&M")) level = "O&M";
		// Accounting access to approve invoice
		else if (roles.includes("_GY:Accounting")) level = "ACC";
		// IT access to resolve invoice
		else if (roles.includes("_GY:IT")) level = "IT";
		// Clients' access to view approved invoice
		else if (roles.includes("administration:InvoiceTable:_clientViewInv"))
			level = "CLIENT";

		return level;
	};

	const viewLevel = validateViewLevel(user.roles);

	/**
	 * Validate sites to be added to Summary Invoice (Makro and CFW client)
	 * @param {Array of sites} arr
	 */
	const getSummaryInvoiceSites = (arr, freeSites) => {
		const unsortedMakroList = arr.filter((site) => site.client_id == 18  && !freeSites.includes(site.table_name));
		const unsortedCFWList = arr.filter((site) => site.client_id == 299);
		const sortedMakroList = unsortedMakroList.sort((a, b) =>
			a.site_code > b.site_code ? 1 : -1
		);const sortedCFWList = unsortedCFWList.sort((a, b) =>
			a.entity_id > b.entity_id ? 1 : -1
		);

		const sortedList = [...sortedMakroList, ...sortedCFWList];
		const formattedSites = sortedList.map((site) => ({
			...site,
			bankAccount: getBankAcc(site),
		}));
		return formattedSites;
	};

	/**
	 * Get bank account info of site
	 * @param {obj} site
	 * @returns Bank account object
	 */
	const getBankAcc = (site) => ({
		accName: site.acc_name,
		accNumber: site.acc_number,
		accNumRemark: site.acc_num_remark,
		bankName: site.bank_name,
		branch: site.branch,
		swiftCode: site.swift_code,
	});

	/**
	 * Fetch Data
	 */
	const fetchData = async (selectedMonth, user, userInfo, country) => {
		if (user != null && userInfo != null) {
			let column = "admin";
			let id = "all";

			// Client Users
			if (user.roles.includes("administration:InvoiceTable:_clientViewInv")) {
				// Multi and single sites IDs
				if (userInfo.site_id != null && userInfo.site_id !== "all") {
					id = userInfo.site_id;
					column = "site_id";
				}
				// Client ID
				else {
					id = userInfo.client_id;
					column = "client_id";
				}
			}

			const lastMonth = selectedMonth;
			const yearMonth = lastMonth.format("YYYYMM");
			const toDate = lastMonth.endOf("month").format("YYYYMMDD");

			// Reasign columns and value for invoice API
			if (column === "admin") {
				column = "country";
				id = country;
			}

			const codURI = `/site/cod/moment/${toDate}/filter/${column}/${id}`;
			const reqCOD = GYAxios.get(codURI);

			// Energy API Request
			const reqEnergyURI = `/invoice/level/${viewLevel}/filter?column=${column}&value=${id}&YYYYMM=${yearMonth}`;
			const reqEnergy = GYAxios.get(reqEnergyURI);

			const reqFreeSites = (isGYUser || (isMakroUser && !isSoloSite)) ? GYAxios.get("/site/free") : "";
			const reqEVNInputs = GYAxios.get(`/evn_input/month/${yearMonth}`);

			const reqArray = [reqCOD, reqEnergy, reqFreeSites];
			if (country === "VN") reqArray.push(reqEVNInputs);

			await Axios.all(reqArray).then(Axios.spread(async (...responses) => {
				let invoiceSites = [];
				const resCODsites = responses[0];
				const resEnergy = responses[1];
				const resFreeSite = (isGYUser || (isMakroUser && !isSoloSite)) ? (responses[2].data.error == null ? responses[2].data.map((freeSite) => freeSite.table_name) : []) : [];
				const resEVNInputs =
					typeof responses[3] !== "undefined" && !responses[3].data.error && country === "VN"
						? responses[3].data : null;

				setFreeSites(resFreeSite);

				if (resCODsites.data != null && resEnergy.data != null) {
					const tempSites = [];
					const COD_edSites = resEnergy.data.filter((site) => {
						return resCODsites.data.some((CODsite) => {
							return CODsite.table_name === site.table_name;
						});
					});

					COD_edSites.forEach((site, id) => {
						const CODsite = resCODsites.data.find((CODsite) => {
							return CODsite.table_name === site.table_name;
						});
						tempSites.push({
							...site,
							contract_id: CODsite.contract_id,
							solo: CODsite.solo,
							index: id,
						});
					});


					/* resCODsites.data.forEach((CODsite, id) => {
						for (let i = 0; i < resEnergy.data.length; i++) {
							const site = resEnergy.data[i];
							if (CODsite.table_name === site.table_name) {
								tempSites.push({
									...site,
									contract_id: CODsite.contract_id,
									solo: CODsite.solo,
									index: id,
								});
								break;
							} 
						}
					}); */

					// Mapping COD sites to the respective energy data
					if (resEVNInputs != null) { // Mapping EVN inputs data to respective sites (ONLY FIT & SCFIT sites)
						const evnTempSites = tempSites.map((site) => {
							if (site.contract_id.includes("FIT")) {
								for (let i = 0; i < resEVNInputs.length; i++) {
									const EVNInputs = resEVNInputs[i];
									if (site.table_name === EVNInputs.table_name) {
										return { ...site, EVNInputs };
									}
								}
							} else return site;
						});

						invoiceSites = evnTempSites;
					} else invoiceSites = tempSites; // SC sites

					const filteredArr = invoiceSites.filter(
						(site) => typeof site != "undefined"
					);

					const SIsites = getSummaryInvoiceSites(filteredArr, resFreeSite);
					setSummaryInvoiceSites(SIsites);
					setInvoiceSites(filteredArr);
				} else {
					setSummaryInvoiceSites(null);
					setInvoiceSites(null);
				}
				setReady(true);
			})
			);
		}
	};

	/**
	 * Fetch country of user and set as initial country
	 */
	const initCountry = (user) => {
		const keycloakID = user.sub;
		GYAxios.get(`/users/filter?column=keycloak_id&value=${keycloakID}`).then(
			(res) => {
				const userInfo =
					res.data.error == null && res.data.length > 0 ? res.data[0] : false;

				setCountry(userInfo.country);
				setUserInfo(userInfo);
			}
		);
	};

	const makroSitesFilterByPhaseFn = useCallback(phase => site => {
		if (phase === 'PHASE_1') {
			return site.site_code.length === 5 && site.client_id == '18'
		} else if (phase === 'PHASE_2') {
			return site.site_code.length !== 5 && site.client_id == '18'
		}
	}, []);

	const CFWSitesFilterByPhaseFn = useCallback(phase => site => {
		if (phase === 'PHASE_GYS6') {
			return site.entity_id == 'GYS6' && site.client_id == '299'
		} else if (phase === 'PHASE_S4') {
			return site.entity_id == 'S4' && site.client_id == '299'
		}
	}, []);

	useEffect(() => {
		if (user != null) {
			validateApprovalLevel(user.roles);
			initCountry(user);
			/* validateMakroUser(user);
			validateCFWUser(user); */
			validateGYUser(user);
		}

		return () => {
			setApprovalPermissions(false);
			setSoloSite(false);
			/* setIsMakroUser(false);
			setIsCFWUser(false); */
			setIsGYUser(false);
		};
	}, []);

	useEffect(() => {
		if (userInfo != null) {
			validateMakroUser(userInfo);
			validateCFWUser(userInfo);
		}

	}, [userInfo]);

	useEffect(() => {
		if (!ready) fetchData(selectedMonth, user, userInfo, country);
	}, [selectedMonth, userInfo, country, ready]);

	return (
		<>
			<InvoiceTable
				ready={ready}
				setReady={setReady}
				country={country}
				setCountry={setCountry}
				freeSites={freeSites}
				approvalPermissions={approvalPermissions}
				selectedMonth={selectedMonth}
				setSelectedMonth={setSelectedMonth}
				invoiceSites={invoiceSites}
				// below will be passed to SummaryInvoice for bulk actions
				makroSitesFilterByPhaseFn={makroSitesFilterByPhaseFn}
				summaryInvoiceSites={summaryInvoiceSites}
				viewLevel={props.viewLevel}
				userInfo={userInfo}
			/>

			{country == "TH" && (isGYUser || (isMakroUser && !isSoloSite) || (isCFWUser && !isSoloSite)) && (
				<>
					{ready ? (
						<SummaryInvoice
							summaryInvoiceSites={summaryInvoiceSites}
							makroSitesFilterByPhaseFn={makroSitesFilterByPhaseFn}
							CFWSitesFilterByPhaseFn={CFWSitesFilterByPhaseFn}
							setSelectedMonth={setSelectedMonth}
							selectedMonth={selectedMonth}
							ready={ready}
							setReady={setReady}
							viewLevel={props.viewLevel}
							whichUser={isGYUser ? "GY" : isMakroUser ? "Makro" : isCFWUser ? "CFW" : ""}
						/>
					) : (
						<LoaderGraph style={{ height: "50vh" }} />
					)}
				</>
			)}
			<Noti />
		</>
	);
}

export default InvoiceContent;